import { isOtherModality } from "@/lib/modalities";

const GENDER_FROM_EXTRACT = { F: "Female", M: "Male", O: "Other" };

const normalizeCompareText = (value) =>
  String(value ?? "").trim().toLowerCase().replace(/\s+/g, " ");

const normalizeCompareGender = (value) => {
  const key = String(value ?? "").trim().toUpperCase();
  if (key === "M" || key === "MALE") return "male";
  if (key === "F" || key === "FEMALE") return "female";
  if (key === "O" || key === "OTHER") return "other";
  return normalizeCompareText(value);
};

const normalizeDateOfBirth = (rawDob) => {
  const value = String(rawDob ?? "").trim();
  if (/^\d{8}$/.test(value)) {
    return `${value.slice(0, 4)}-${value.slice(4, 6)}-${value.slice(6, 8)}`;
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  return undefined;
};

const ageFromDateOfBirth = (rawDob) => {
  const iso = normalizeDateOfBirth(rawDob);
  if (!iso) return "";
  const [yearStr, monthStr, dayStr] = iso.split("-");
  const birthDate = new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr));
  if (Number.isNaN(birthDate.getTime())) return "";
  const today = new Date();
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age -= 1;
  }
  return age >= 0 ? String(age) : "";
};

const identityFromExtractDetails = (details) => {
  if (!details) return null;
  const genderKey = String(details.gender ?? "").trim().toUpperCase();
  const gender = GENDER_FROM_EXTRACT[genderKey] || details.gender || "";
  const age = details.age != null && details.age !== ""
    ? String(details.age)
    : ageFromDateOfBirth(details.date_of_birth);
  return {
    uhid: details.system_uhid || "",
    external_uhid: details.patient_id || "",
    name: details.name || "",
    gender,
    age,
  };
};

const identityFromPatient = (patient) => {
  if (!patient) return null;
  return {
    uhid: patient.uhid || "",
    external_uhid: patient.external_uhid || "",
    name: patient.name || "",
    gender: patient.gender || "",
    age: patient.age != null && patient.age !== "" ? String(patient.age) : "",
  };
};

const hasFullLockedIdentity = (patient) => Boolean(
  String(patient?.name ?? "").trim()
  && String(patient?.age ?? "").trim()
  && String(patient?.gender ?? "").trim(),
);

/** Snapshot of the Add Study patient so extract can classify after the sheet closes. */
export const snapshotLockedPatient = (patient) => {
  if (!patient?.uhid) return null;
  return {
    uhid: patient.uhid || "",
    external_uhid: patient.external_uhid || "",
    name: patient.name || "",
    gender: patient.gender || "",
    age: patient.age != null && patient.age !== "" ? String(patient.age) : "",
  };
};

/** Same patient when name, age, and gender all match. UHID is ignored. */
export const isSamePatientAsLocked = (lockedPatient, extractDetails) => {
  const current = identityFromPatient(lockedPatient);
  const extracted = identityFromExtractDetails(extractDetails);
  if (!current || !extracted) return false;
  if (
    !normalizeCompareText(current.name)
    || !normalizeCompareText(extracted.name)
    || normalizeCompareText(current.name) !== normalizeCompareText(extracted.name)
  ) {
    return false;
  }
  if (
    !current.age
    || !extracted.age
    || String(current.age) !== String(extracted.age)
  ) {
    return false;
  }
  if (
    !normalizeCompareGender(current.gender)
    || !normalizeCompareGender(extracted.gender)
    || normalizeCompareGender(current.gender) !== normalizeCompareGender(extracted.gender)
  ) {
    return false;
  }
  return true;
};

/**
 * Stamp Add Study extract as match / mismatch without waiting for the Add Study sheet.
 * Returns null when there is not enough data to decide.
 */
export const classifyAddStudyExtract = (lockedPatient, details, file) => {
  const lockedUhid = String(lockedPatient?.uhid || "").trim();
  if (isOtherModality(file?.modality) && lockedUhid) {
    return {
      patient_mismatch: false,
      mismatch_kind: "",
      mismatch_patient_uhid: "",
    };
  }
  if (!details) return null;
  if (!lockedUhid) return null;

  const systemUhid = String(details.system_uhid || "").trim();
  const locked = { uhid: lockedUhid, ...lockedPatient };

  if (hasFullLockedIdentity(locked) && isSamePatientAsLocked(locked, details)) {
    return {
      patient_mismatch: false,
      mismatch_kind: "",
      mismatch_patient_uhid: "",
    };
  }

  if (hasFullLockedIdentity(locked)) {
    if (details.exists === true) {
      return {
        patient_mismatch: true,
        mismatch_kind: "add_study_exists",
        mismatch_patient_uhid: systemUhid || lockedUhid,
      };
    }
    return {
      patient_mismatch: true,
      mismatch_kind: "add_study_new",
      mismatch_patient_uhid: lockedUhid,
    };
  }

  // Snapshot without name/age/gender: cannot decide match vs mismatch.
  return null;
};

export const isAddStudyQueueFile = (file) => (
  file?.origin === "add_study"
  || file?.queue === "add_study"
  || Boolean(file?.locked_patient_uhid)
);

/** Extracted Add Study file that should open the exists/mismatch popup, not Add Study. */
export const needsAddStudyMismatchReview = (file) => {
  if (file?.status !== "extracted") return false;
  if (isOtherModality(file?.modality)) return false;
  if (!isAddStudyQueueFile(file)) return false;
  if (file.patient_mismatch) return true;
  const locked = file.locked_patient || { uhid: file.locked_patient_uhid };
  const classified = classifyAddStudyExtract(locked, file.patient_details, file);
  return Boolean(classified?.patient_mismatch);
};
